import { useState, useEffect } from "react";
import {
    Box,
    Typography,
    AppBar,
    Toolbar,
    IconButton,
    Menu,
    MenuItem,
    Card,
    CardContent,
    Chip,
    Alert,
    Slide,
    Fade,
    CircularProgress,
    Button,
} from "@mui/material";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom"; // ✅ to get queueNumber
import FMBlogo from "../../../assests/FMBlogo.png";
import ScrollButtons from "../../../components/ScrollButtons";
import Logo from "../../../assests/logo.png";
// Demo images for portrait ads
import DemoPortrait1 from "../../../assests/Potrait/1.png";
import DemoPortrait2 from "../../../assests/Potrait/2.png";
import DemoPortrait3 from "../../../assests/Potrait/3.jpg";
import DemoPortrait4 from "../../../assests/Potrait/4.jpg";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import MenuIcon from "@mui/icons-material/Menu";
import {
    createPusherInstance,
    subscribeToQueueUpdates,
} from "../../../config/pusher";
import QueueManager from "../../../utils/QueueManager";
import { getActiveAds, getQueue, getAllServices } from "../../../api/api";
import Cookies from "js-cookie";

// Demo ads for demo mode (defined outside component to avoid recreation)
const demoAds = [
    {
        id: "demo-1",
        title: "Demo Ad 1",
        image_url: DemoPortrait1,
        description: "Sample portrait advertisement",
    },
    {
        id: "demo-2",
        title: "Demo Ad 2",
        image_url: DemoPortrait2,
        description: "Sample portrait advertisement",
    },
    {
        id: "demo-3",
        title: "Demo Ad 3",
        image_url: DemoPortrait3,
        description: "Sample portrait advertisement",
    },
    {
        id: "demo-4",
        title: "Demo Ad 4",
        image_url: DemoPortrait4,
        description: "Sample portrait advertisement",
    },
];

const QueueDisplay = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    // Get data from URL parameters (for SMS tracking)
    const urlQueueId = searchParams.get("queue_id");
    const urlQueueNumber = searchParams.get("queue_number");
    const urlBranchId = searchParams.get("branch_id");
    const urlPhone = searchParams.get("phone");
    const urlServiceIds = searchParams.get("service_ids");

    // Get data from location state (for QR flow)
    const {
        queueNumber: stateQueueNumber = "00",
        selectedOptions: stateSelectedOptions = [],
        ticketCode: stateTicketCode = "DEMO",
        isDemoMode: stateIsDemoMode = true,
        existing: stateExisting = false,
    } = location.state || {};

    // Use URL parameters if available (SMS flow), otherwise use state (QR flow)
    const queueNumber = urlQueueNumber || stateQueueNumber;
    const isSmsFlow = !!urlQueueId;

    // Convert service IDs to selected options for SMS flow
    const [smsSelectedOptions, setSmsSelectedOptions] = useState([]);

    // Load services for SMS flow
    useEffect(() => {
        const loadServicesForSms = async () => {
            if (isSmsFlow && urlServiceIds && urlBranchId) {
                try {
                    const services = await getAllServices(urlBranchId);

                    // Convert service IDs to service objects
                    const serviceIdArray = urlServiceIds
                        .split(",")
                        .filter((id) => id.trim() !== "");
                    const selectedServices = services.filter((service) =>
                        serviceIdArray.includes(service.id.toString())
                    );

                    console.log(
                        "SMS: Loaded services for display:",
                        selectedServices
                    );
                    setSmsSelectedOptions(selectedServices);
                } catch (error) {
                    console.error("SMS: Failed to load services:", error);
                }
            }
        };

        loadServicesForSms();
    }, [isSmsFlow, urlServiceIds, urlBranchId]);

    // Use SMS selected options if available, otherwise use state selected options
    const selectedOptions = isSmsFlow
        ? smsSelectedOptions
        : stateSelectedOptions;

    // For SMS flow, use live mode (not demo)
    // Check if ticket code is DEMO to determine demo mode (more reliable than state)
    const isDemoModeFromTicket = stateTicketCode === "DEMO";
    const isDemoMode = isSmsFlow ? false : (stateIsDemoMode !== undefined ? stateIsDemoMode : isDemoModeFromTicket);
    const ticketCode = isSmsFlow ? "SMS" : stateTicketCode;
    const existing = isSmsFlow ? true : stateExisting || false;

    // Log the received data for debugging
    console.log("NextCustomerView received selectedOptions:", selectedOptions);
    console.log("Flow type:", isSmsFlow ? "SMS" : "QR");
    console.log("Mode:", isDemoMode ? "DEMO" : "LIVE");
    console.log("Ticket code:", ticketCode);
    console.log("Existing:", existing);
    console.log("URL parameters:", {
        urlQueueId,
        urlQueueNumber,
        urlBranchId,
        urlPhone,
        urlServiceIds,
    });
    console.log("State data:", {
        stateQueueNumber,
        stateSelectedOptions,
        stateTicketCode,
        stateIsDemoMode,
        stateExisting,
    });

    const [currentIndex, setCurrentIndex] = useState(0);
    const [anchorEl, setAnchorEl] = useState(null);
    const [isBeingServed, setIsBeingServed] = useState(false);
    const [ads, setAds] = useState([]);
    const [queueStatus, setQueueStatus] = useState(null);
    const [isLoadingQueue, setIsLoadingQueue] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const open = Boolean(anchorEl);

    // Initialize demo ads immediately if in demo mode
    useEffect(() => {
        if (isDemoMode && ads.length === 0) {
            console.log("Demo mode detected - initializing demo ads immediately");
            setAds(demoAds);
            setLoading(false);
        }
    }, [isDemoMode, ads.length]);

    // Fetch portrait ads from database
    const fetchPortraitAds = async () => {
        // Skip API call if in demo mode - use demo ads directly
        if (isDemoMode) {
            console.log("Demo mode: Using demo ads (skipping API call)");
            setAds(demoAds);
            setLoading(false);
            setError(null);
            return;
        }

        try {
            setLoading(true);
            const portraitAds = await getActiveAds("portrait");

            if (portraitAds.success && portraitAds.data.length > 0) {
                setAds(portraitAds.data);
            } else {
                setAds([]);
            }
            setError(null);
        } catch (err) {
            console.error("Error fetching portrait ads:", err);
            setError("Failed to load ads");
            setAds([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchPortraitAds();
    }, [isDemoMode]);

    useEffect(() => {
        if (ads.length > 0) {
            const interval = setInterval(() => {
                setCurrentIndex((prevIndex) => (prevIndex + 1) % ads.length);
            }, 5000); // Change image every 5 seconds
            return () => clearInterval(interval);
        }
    }, [ads]);

    // Check queue status for SMS flow (only once on load)
    useEffect(() => {
        const checkQueueStatus = async () => {
            if (isSmsFlow && urlQueueId) {
                setIsLoadingQueue(true);
                try {
                    const response = await getQueue(urlQueueId);
                    const queue = response.queue;
                    setQueueStatus(queue.QueueStatus);

                    // If queue is already served, redirect to thank you page
                    if (queue.QueueStatus === "ASSISTED") {
                        console.log(
                            "Queue already served, redirecting to thank you page"
                        );
                        console.log("Queue status:", queue.QueueStatus);
                        console.log("Queue ID:", queue.QueueID);
                        console.log("Queue Number:", queue.QueueNr);
                        navigate("/home/thank-you", {
                            state: {
                                queueNumber,
                                selectedOptions,
                                ticketCode,
                                isDemoMode,
                                existing: true,
                                isSmsFlow: true,
                            },
                        });
                    } else {
                        console.log(
                            "Queue status is not ASSISTED:",
                            queue.QueueStatus
                        );
                    }
                } catch (err) {
                    console.error("Failed to check queue status", err);
                } finally {
                    setIsLoadingQueue(false);
                }
            }
        };

        checkQueueStatus();
    }, [isSmsFlow, urlQueueId]);

    // Pusher event handling for queue status updates
    useEffect(() => {
        if (isDemoMode || !ticketCode) return;

        console.log("🔌 Initializing Pusher for SMS flow:", {
            isDemoMode,
            ticketCode,
            isSmsFlow,
            urlQueueId,
            queueNumber,
        });

        // Initialize Pusher
        const pusher = createPusherInstance();

        // Subscribe to the queue channel
        const channel = subscribeToQueueUpdates(pusher, (data) => {
            console.log("📡 Queue update received in SMS flow:", data);
            // Handle queue updates for SMS flow
        });

        // Listen for queue updates
        channel.bind("queue.updated", (data) => {
            console.log("📨 NextCustomerView: Queue update received:", data);
            console.log("📨 NextCustomerView: Our queue number:", queueNumber);
            console.log("📨 NextCustomerView: Our queue ID (SMS):", urlQueueId);
            console.log("📨 NextCustomerView: Our ticket code:", ticketCode);

            // Check if this is for our queue
            if (data.queue) {
                const updatedQueue = data.queue;
                const action = data.action;

                console.log(
                    `📋 NextCustomerView: ${action} queue number:`,
                    updatedQueue.QueueNr
                );
                console.log(
                    `📋 NextCustomerView: ${action} queue ID:`,
                    updatedQueue.QueueID
                );
                console.log(
                    `📋 NextCustomerView: ${action} queue status:`,
                    updatedQueue.QueueStatus
                );

                // Check if this is our queue by queue number or queue ID
                const queueNumberMatch = updatedQueue.QueueNr == queueNumber;
                const queueIdMatch =
                    isSmsFlow && updatedQueue.QueueID == parseInt(urlQueueId);
                const isOurQueue = queueNumberMatch || queueIdMatch;

                console.log(
                    "🔍 NextCustomerView: Queue number match:",
                    queueNumberMatch
                );
                console.log(
                    "🔍 NextCustomerView: Queue ID match (SMS):",
                    queueIdMatch
                );
                console.log("🔍 NextCustomerView: Is SMS flow:", isSmsFlow);
                console.log("🔍 NextCustomerView: URL queue ID:", urlQueueId);
                console.log("✅ NextCustomerView: Is our queue?", isOurQueue);

                if (isOurQueue) {
                    if (action === "serving") {
                        console.log(
                            "🟡 NextCustomerView: Our queue is now being served"
                        );
                        setIsBeingServed(true);
                    } else if (action === "assisted") {
                        console.log(
                            "🎉 NextCustomerView: Our queue marked as assisted, showing thank you page"
                        );

                        // Clear queue data from localStorage
                        QueueManager.clearQueue(ticketCode);

                        // Get ticketData from cookies if available, or construct from available data
                        const ticketDetails = Cookies.get("ticketDetails");
                        let ticketData = null;
                        if (ticketDetails) {
                            try {
                                ticketData = JSON.parse(ticketDetails);
                            } catch (e) {
                                console.error("Error parsing ticketDetails:", e);
                            }
                        }
                        
                        // If no ticketData from cookies, construct minimal one with branchId
                        if (!ticketData && urlBranchId) {
                            ticketData = {
                                client: {
                                    id: parseInt(urlBranchId)
                                },
                                ticket_code: ticketCode
                            };
                        }

                        // Navigate to thank you page for both SMS and QR flows
                        navigate("/home/thank-you", {
                            state: {
                                queueNumber,
                                selectedOptions,
                                ticketCode,
                                isDemoMode,
                                existing: true,
                                isSmsFlow: isSmsFlow,
                                ticketData: ticketData // Pass ticketData with logo info
                            },
                        });
                    }
                } else {
                    console.log(
                        "❌ NextCustomerView: Not our queue, ignoring event - QueueNR:",
                        updatedQueue.QueueNr,
                        "Our queue:",
                        queueNumber,
                        "QueueID:",
                        updatedQueue.QueueID,
                        "Our URL QueueID:",
                        urlQueueId
                    );
                }
            }
        });

        // Cleanup
        return () => {
            channel.unbind("queue.updated");
            pusher.unsubscribe("queues");
            pusher.disconnect();
        };
    }, [ticketCode, isDemoMode, queueNumber, selectedOptions, navigate]);

    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };
    const handleClose = () => {
        setAnchorEl(null);
    };

    return (
        <>
            <Box sx={{ flexGrow: 1 }}>
                <AppBar position="static">
                    <Toolbar>
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                mr: 2,
                            }}
                        >
                            <img
                                src={Logo}
                                alt="Logo"
                                style={{
                                    height: "32px",
                                    marginRight: "16px",
                                    filter: "brightness(0) invert(1)", // Make logo white
                                }}
                            />
                        </Box>
                        <Typography
                            variant="h6"
                            component="div"
                            sx={{ flexGrow: 1 }}
                        >
                            <span
                                style={{
                                    color: isDemoMode ? "#1976d2" : "#2e7d32",
                                    fontWeight: "bold",
                                }}
                            >
                                {isDemoMode ? "DEMO MODE" : ""}
                            </span>
                        </Typography>

                        <IconButton
                            size="large"
                            color="inherit"
                            aria-label="menu"
                            sx={{ ml: 2 }}
                            onClick={handleClick}
                        >
                            <MenuIcon />
                        </IconButton>

                        {/* Top menu */}
                        <Menu
                            anchorEl={anchorEl}
                            open={open}
                            onClose={handleClose}
                            anchorOrigin={{
                                vertical: "top",
                                horizontal: "right",
                            }}
                            transformOrigin={{
                                vertical: "top",
                                horizontal: "right",
                            }}
                        >
                            <MenuItem onClick={handleClose}>
                                <a
                                    href="/home/panel"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{
                                        textDecoration: "none",
                                        color: "inherit",
                                    }}
                                >
                                    HOME
                                </a>
                            </MenuItem>
                            <MenuItem onClick={handleClose}>
                                <a
                                    href="https://www.facebook.com/MajorstuenFargerike/"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{
                                        textDecoration: "none",
                                        color: "inherit",
                                    }}
                                >
                                    FACEBOOK
                                </a>
                            </MenuItem>
                            <MenuItem onClick={handleClose}>
                                <a
                                    href="https://www.instagram.com/fargerike_majorstuen/"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{
                                        textDecoration: "none",
                                        color: "inherit",
                                    }}
                                >
                                    INSTAGRAM
                                </a>
                            </MenuItem>
                            <MenuItem onClick={handleClose}>
                                <a
                                    href="https://fargerikemajorstuen.no/"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{
                                        textDecoration: "none",
                                        color: "inherit",
                                    }}
                                >
                                    WEB
                                </a>
                            </MenuItem>
                            <MenuItem onClick={handleClose}>
                                <a
                                    href="https://admin.easyque.eu/carousel_landscape.php?client_id=1"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{
                                        textDecoration: "none",
                                        color: "inherit",
                                    }}
                                >
                                    KAMPANJER
                                </a>
                            </MenuItem>
                            <MenuItem onClick={handleClose}>
                                <a
                                    href="https://maps.app.goo.gl/Gcg4tKiw7cs7wRis9"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{
                                        textDecoration: "none",
                                        color: "inherit",
                                    }}
                                >
                                    PARKERING/KART
                                </a>
                            </MenuItem>
                            <MenuItem onClick={handleClose}>
                                <a
                                    href="https://easy.fabconcept.no/kontakt_logo_fmb/"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{
                                        textDecoration: "none",
                                        color: "inherit",
                                    }}
                                >
                                    KONTAKT
                                </a>
                            </MenuItem>
                            <MenuItem onClick={handleClose}>
                                <a
                                    href="https://easy.fabconcept.no/prosjekt-fmb"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{
                                        textDecoration: "none",
                                        color: "inherit",
                                    }}
                                >
                                    PROSJEKT
                                </a>
                            </MenuItem>
                        </Menu>
                    </Toolbar>
                </AppBar>
            </Box>

            {/* Main content */}
            <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                p={4}
            >
                {/* Logo */}
                <img src={FMBlogo} width={300} alt="fmblogo" />
                <Typography variant="subtitle1">Majorstuen</Typography>

                {/* Loading indicator for queue status check */}
                {isLoadingQueue && (
                    <Box display="flex" alignItems="center" gap={2} mt={2}>
                        <CircularProgress size={24} />
                        <Typography variant="body2" color="text.secondary">
                            Checking queue status...
                        </Typography>
                    </Box>
                )}

                <Typography variant="h6" fontWeight="bold" mt={2}>
                    Ditt kønummer er
                </Typography>

                {/* ✅ Real queue number */}
                <Typography variant="h1" fontWeight="bold">
                    {queueNumber}
                </Typography>

                {/* Serving Status Banner */}
                <Slide
                    direction="down"
                    in={isBeingServed}
                    mountOnEnter
                    unmountOnExit
                >
                    <Alert
                        severity="success"
                        sx={{
                            mt: 2,
                            mb: 2,
                            fontSize: "1.2rem",
                            fontWeight: "bold",
                            borderRadius: 2,
                            boxShadow: 3,
                            animation: "pulse 2s infinite",
                            "@keyframes pulse": {
                                "0%": { transform: "scale(1)" },
                                "50%": { transform: "scale(1.05)" },
                                "100%": { transform: "scale(1)" },
                            },
                        }}
                    >
                        Du blir nå betjent!
                    </Alert>
                </Slide>

                {/* Demo Mode Indicator */}
                <Typography
                    variant="body2"
                    color="primary"
                    fontWeight="bold"
                    mt={1}
                >
                    {isDemoMode ? "DEMO MODE - Ingen faktisk kø generert" : ""}
                </Typography>

                {/* Optional: Show selected services */}
                {selectedOptions.length > 0 && (
                    <Card
                        sx={{
                            mt: 2,
                            mb: 2,
                            maxWidth: 600,
                            width: "100%",
                            boxShadow: 2,
                            borderRadius: 2,
                        }}
                    >
                        <CardContent>
                            <Typography
                                variant="h6"
                                fontWeight="bold"
                                textAlign="center"
                                mb={3}
                                color="primary"
                            >
                                Valgte Tjenester
                            </Typography>
                            <Box
                                display="grid"
                                gridTemplateColumns="repeat(auto-fit, minmax(200px, 1fr))"
                                gap={2}
                                justifyContent="center"
                            >
                                {selectedOptions.map((service, index) => (
                                    <Card
                                        key={index}
                                        sx={{
                                            border: "1px solid",
                                            borderColor: "grey.300",
                                            borderRadius: 2,
                                            boxShadow: "none",
                                            backgroundColor: "white",
                                            transition: "all 0.2s ease-in-out",
                                            "&:hover": {
                                                boxShadow: 1,
                                                transform: "translateY(-2px)",
                                            },
                                        }}
                                    >
                                        <CardContent
                                            sx={{
                                                padding: 2,
                                                textAlign: "center",
                                            }}
                                        >
                                            <Typography
                                                variant="h6"
                                                fontWeight="bold"
                                                color="text.primary"
                                                sx={{
                                                    fontSize: "1.1rem",
                                                    marginBottom: 1,
                                                }}
                                            >
                                                {service.service_name ||
                                                    service}
                                            </Typography>
                                            {service.service_tag && (
                                                <Typography
                                                    variant="body2"
                                                    color="text.secondary"
                                                    sx={{
                                                        fontSize: "0.85rem",
                                                        lineHeight: 1.4,
                                                    }}
                                                >
                                                    {service.service_tag}
                                                </Typography>
                                            )}
                                        </CardContent>
                                    </Card>
                                ))}
                            </Box>
                        </CardContent>
                    </Card>
                )}

                {/* Waiting Time Message */}
                <Typography variant="body1" mt={1} textAlign="center">
                    Vi hjelper deg gjerne i gang opptil 10 min.
                </Typography>

                {/* Service Recommendation */}
                <Typography
                    variant="body2"
                    mt={1}
                    textAlign="center"
                    maxWidth={500}
                >
                    Mange ønsker hjelp med Fargeveiledning. Vi anbefaler at du
                    booker en time med oss til kun kr. 690,- for 45 min med
                    personlig veiledning, fargepalett og interiørplan.
                </Typography>

                {/* Rotating Banner */}
                <Box
                    mt={3}
                    sx={{
                        width: { xs: "95%", sm: "90%", md: "80%", lg: "70%" },
                        maxWidth: { xs: "100%", sm: 600, md: 700, lg: 800 },
                        margin: "0 auto",
                        px: { xs: 1, sm: 2 }, // Add horizontal padding for mobile
                    }}
                >
                    {loading ? (
                        <Box
                            display="flex"
                            justifyContent="center"
                            alignItems="center"
                            sx={{
                                height: {
                                    xs: "250px",
                                    sm: "300px",
                                    md: "350px",
                                    lg: "400px",
                                },
                                minHeight: {
                                    xs: "200px",
                                    sm: "250px",
                                    md: "300px",
                                    lg: "350px",
                                },
                            }}
                        >
                            <CircularProgress
                                size={{ xs: 40, sm: 50, md: 60 }}
                            />
                        </Box>
                    ) : error ? (
                        <Box
                            display="flex"
                            justifyContent="center"
                            alignItems="center"
                            sx={{
                                height: {
                                    xs: "250px",
                                    sm: "300px",
                                    md: "350px",
                                    lg: "400px",
                                },
                                minHeight: {
                                    xs: "200px",
                                    sm: "250px",
                                    md: "300px",
                                    lg: "350px",
                                },
                                backgroundColor: "#f5f5f5",
                                borderRadius: {
                                    xs: "4px",
                                    sm: "6px",
                                    md: "8px",
                                },
                                border: "2px dashed #ccc",
                            }}
                        >
                            <Typography
                                color="error"
                                variant="body2"
                                sx={{
                                    textAlign: "center",
                                    px: 2,
                                    fontSize: { xs: "0.875rem", sm: "1rem" },
                                }}
                            >
                                {error}
                            </Typography>
                        </Box>
                    ) : ads.length > 0 ? (
                        <Box
                            sx={{
                                width: "100%",
                                height: {
                                    xs: "250px",
                                    sm: "300px",
                                    md: "350px",
                                    lg: "400px",
                                },
                                maxHeight: {
                                    xs: "300px",
                                    sm: "350px",
                                    md: "400px",
                                    lg: "450px",
                                },
                                borderRadius: {
                                    xs: "4px",
                                    sm: "6px",
                                    md: "8px",
                                },
                                overflow: "hidden",
                                position: "relative",
                                backgroundColor: "#f8f9fa", // Light background for image container
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                            }}
                        >
                            <img
                                src={ads[currentIndex]?.image_url}
                                alt={
                                    ads[currentIndex]?.title ||
                                    `Portrait Ad ${currentIndex + 1}`
                                }
                                style={{
                                    width: "100%",
                                    height: "100%",
                                    objectFit: "contain",
                                    transition: "opacity 1s ease-in-out",
                                    display: "block",
                                }}
                            />
                        </Box>
                    ) : (
                        <Box
                            display="flex"
                            justifyContent="center"
                            alignItems="center"
                            sx={{
                                height: {
                                    xs: "250px",
                                    sm: "300px",
                                    md: "350px",
                                    lg: "400px",
                                },
                                minHeight: {
                                    xs: "200px",
                                    sm: "250px",
                                    md: "300px",
                                    lg: "350px",
                                },
                                backgroundColor: "#f5f5f5",
                                borderRadius: {
                                    xs: "4px",
                                    sm: "6px",
                                    md: "8px",
                                },
                                border: "2px dashed #ccc",
                            }}
                        >
                            <Typography
                                color="text.secondary"
                                variant="body2"
                                sx={{
                                    textAlign: "center",
                                    px: 2,
                                    fontSize: { xs: "0.875rem", sm: "1rem" },
                                }}
                            >
                                {isDemoMode
                                    ? "Demo Mode - Loading Ads..."
                                    : "No Portrait Ads Available"}
                            </Typography>
                        </Box>
                    )}
                </Box>

                {/* Footer */}
                <Typography variant="body2" color="textSecondary" mt={3}>
                    easyQue (c) by Digifront
                </Typography>
                <Typography variant="body2" color="primary">
                    sales@digifront.biz
                </Typography>
            </Box>
            <ScrollButtons />
        </>
    );
};

export default QueueDisplay;
